import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useChatContext } from './ChatContext';
import { ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from '@/components/icons';
import { openInNewTab, renderUrlTemplate } from '@/utils/url-template';
import {
  discountTag,
  finalPrice,
  formatDetail,
  formatName,
  formatPrice,
  priceLead,
  useChosenVariant,
} from '@/components/ProductCard';
import { MarkdownRenderer } from '@/markdown/renderer';
import { detectProductType } from '@/utils/productType';
import { imageSrcSet, sizedImage } from '@/utils/images';
import { useDragScroll } from '@/utils/useDragScroll';
import { Lightbox, type Slide } from './Lightbox';
import type { Product, ProductVariant } from '@/types/chat';

export interface ProductPreviewPaneProps {
  product: Product;
  onClose: () => void;
  /**
   * When true, the panel fills its parent height (e.g. a 100vh side panel)
   * and the body uses `flex: 1` to absorb extra space, pinning the footer
   * to the bottom. When false (default), the panel sizes to its content
   * and the parent — typically the mobile bottom drawer — caps it.
   */
  fillHeight?: boolean;
}

/**
 * The product sheet, laid out like the product page on a phone: the photo carousel,
 * title, price, benefits, then everything the payload says about the product —
 * description, ingredients, usage, precautions — as accordions. The format and
 * "Ajouter au panier" sit together in the footer. Styles: .cal-pv in styles/index.css.
 */
export function ProductPreviewPane({
  product,
  onClose,
  fillHeight = false,
}: ProductPreviewPaneProps): JSX.Element {
  const { onProductDetailsClick, productPageUrl } = useChatContext();
  // Same format as the product's card: choosing here changes it there too.
  const [variant, choose] = useChosenVariant(product);
  const several = product.variants.length > 1;

  function handleViewFullPage() {
    if (onProductDetailsClick) {
      onProductDetailsClick(product.id, product.slug);
    } else if (productPageUrl) {
      openInNewTab(renderUrlTemplate(productPageUrl, { slug: product.slug }));
    } else {
      openInNewTab(product.url);
    }
  }

  // 没有 variant = 这条数据来自老 session 快照，价格和库存都不可信：
  // 宁可不显示，也不要显示 0,00 €。同 ProductCard。
  const final = variant ? finalPrice(variant) : undefined;
  const priceLabel = final != null ? formatPrice(final) : null;
  const lead = variant ? priceLead(variant) : [];
  // families[0] can be a promotional tag ("Jusqua 30percent"), so the type comes
  // from detectProductType (the card's badge uses it too). The format shows once:
  // here for a single-format product, in the footer's field when there are several.
  const productType = detectProductType(product);
  const variantTagLine = several
    ? productType?.toUpperCase()
    : (productType
        ? [productType, variant && formatName(variant.title)].filter(Boolean).join(' · ')
        : (variant && formatName(variant.title)) || ''
      ).toUpperCase() || 'PRODUIT';
  const otherNames = [product.chineseTitle, product.pinyinTitle].filter(Boolean).join(' · ');

  return (
    // `tw-min-h-0` lets the aside shrink below its content height when a constrained
    // flex parent (the mobile drawer at 85vh) demands it; `tw-h-full` is opt-in for
    // fillHeight (the desktop side panel fills its 100vh wrapper, footer at the bottom).
    <aside
      className={`cal-pv tw-flex tw-flex-col tw-w-full tw-min-h-0 tw-bg-white tw-border-l tw-border-cal-green-100 ${
        fillHeight ? 'tw-h-full' : ''
      }`}
    >
      {/* Header — fixed at top of the panel, never shrinks. */}
      <header className="tw-flex-shrink-0 tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-cal-green-100">
        <button type="button" onClick={handleViewFullPage} className="cal-pv-cta gtmtrack" content-content="chatbot-fiche-page-produit">
          Fiche produit
          <ArrowUpRightIcon width={13} height={13} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer l'aperçu"
          content-content="chatbot-fiche-fermer"
          className="gtmtrack tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-cal-green-700 hover:tw-bg-cal-green-50 tw-transition-colors"
        >
          <XIcon width={14} height={14} />
        </button>
      </header>

      {/* Body — the only part that scrolls; with fillHeight it absorbs the remaining
          height so the footer stays at the bottom of the 100vh side panel. */}
      <div className={`cal-pv-body tw-min-h-0 tw-overflow-y-auto tw-overscroll-contain ${fillHeight ? 'tw-flex-1' : ''}`}>
        <Gallery product={product} variant={variant} />

        <div>
          {variantTagLine && <p className="cal-pv-tag">{variantTagLine}</p>}
          <h2 className="cal-pv-title">
            <button type="button" onClick={handleViewFullPage} className="gtmtrack" content-content="chatbot-fiche-produit">
              {product.title}
              <ArrowUpRightIcon width={16} height={16} strokeWidth={2.5} />
            </button>
          </h2>
          {otherNames && <p className="cal-pv-names">{otherNames}</p>}
          {product.reviews && product.reviews.count > 0 && (
            <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-cal-green-700">
              <Stars rating={product.reviews.rating} />
              <span>
                · {product.reviews.rating.toFixed(1)} / 5 ({product.reviews.count} avis)
              </span>
            </div>
          )}
        </div>

        {/* Price — no variant means legacy-snapshot data: no fabricated "0,00 €",
            and no Rupture badge either (a missing variant isn't out of stock). */}
        {(priceLabel || lead.length > 0) && (
          <div className="cal-pv-price">
            {lead.length > 0 && <span className="cal-pcard-was">{lead}</span>}
            {priceLabel && <span className="cal-pv-now">{priceLabel}</span>}
          </div>
        )}

        {product.foreword && <p className="cal-pv-foreword">{product.foreword}</p>}

        {product.effects && product.effects.length > 0 && (
          <div>
            <h3 className="cal-pv-h3">Bienfaits</h3>
            <ul className="cal-pv-benefits">
              {product.effects.map((effect, i) => (
                <li key={i}>
                  <CheckIcon />
                  <span>{humanizeEffect(effect)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* key: another product starts with every section closed */}
        <SheetSections key={product.slug} sections={sheetSections(product)} />

      </div>

      {variant && <BuyBar key={product.slug} variants={product.variants} variant={variant} onChoose={choose} />}
    </aside>
  );
}

/**
 * The product page's photos, then each format's own photo not already among them —
 * like the product page, whose carousel also shows the formats' photos.
 */
function gallerySlides(p: Product): Slide[] {
  const slides: Slide[] = (p.images ?? []).map((im) => ({ url: im.url, formats: im.formats, width: im.width }));
  for (const v of p.variants) {
    if (v.image && !slides.some((s) => s.url === v.image)) slides.push({ url: v.image, formats: v.imageFormats });
  }
  if (slides.length === 0 && p.image) slides.push({ url: p.image });
  return slides;
}

/**
 * The photo carousel, as on the product page: swipe (native scroll-snap), a mouse
 * drag or the arrows, "n / N", a row of thumbnails. Choosing a format brings up its photo;
 * a click on a photo blows it up (Lightbox), and closing that comes back on the photo it left.
 */
function Gallery({ product, variant }: { product: Product; variant: ProductVariant | undefined }): JSX.Element {
  const slides = gallerySlides(product);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const dragScroll = useDragScroll((track) => track.clientWidth);

  function goTo(i: number, smooth = true) {
    setIndex(i);
    const track = trackRef.current;
    track?.scrollTo?.({ left: i * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
  }

  // a newly chosen format shows its own photo (the first render keeps photo 1, like the page)
  const shownVariant = useRef(variant?.id);
  useEffect(() => {
    if (variant?.id === shownVariant.current) return;
    shownVariant.current = variant?.id;
    const i = slides.findIndex((s) => s.url === variant?.image);
    if (i >= 0) goTo(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant?.id]);

  // keep the current thumbnail in view
  useEffect(() => {
    const row = thumbsRef.current;
    const thumb = row?.children[index] as HTMLElement | undefined;
    if (row && thumb) row.scrollTo?.({ left: thumb.offsetLeft - (row.clientWidth - thumb.clientWidth) / 2, behavior: 'smooth' });
  }, [index]);

  const many = slides.length > 1;
  if (slides.length === 0) {
    return <div className="cal-pv-stage cal-pv-empty">{product.title}</div>;
  }
  return (
    <div className="cal-pv-gallery">
      <div className="cal-pv-stage">
        <div
          ref={trackRef}
          className={`cal-pv-track${many ? ' is-scrollable' : ''}`}
          {...dragScroll}
          onScroll={(e) => {
            const t = e.currentTarget;
            if (t.clientWidth) setIndex(Math.round(t.scrollLeft / t.clientWidth));
          }}
        >
          {slides.map((s, i) => (
            <div key={s.url} className="cal-pv-slide">
              <button
                type="button"
                aria-label={`Agrandir la photo ${i + 1}`}
                onClick={() => setZoomed(i)}
                className="cal-pv-zoom gtmtrack"
                content-content="chatbot-fiche-photo-agrandir"
              >
                <img
                  src={sizedImage(s.url, s.formats, 'medium')}
                  srcSet={imageSrcSet(s.url, s.formats, s.width)}
                  sizes="(min-width: 640px) 384px, 100vw"
                  alt=""
                  loading={i === 0 ? 'eager' : 'lazy'}
                  draggable={false}
                />
              </button>
            </div>
          ))}
        </div>
        {many && (
          <>
            <button type="button" aria-label="Photo précédente" disabled={index === 0} onClick={() => goTo(index - 1)} className="cal-pv-arrow is-prev gtmtrack" content-content="chatbot-fiche-photo">
              <ChevronLeftIcon width={18} height={18} />
            </button>
            <button type="button" aria-label="Photo suivante" disabled={index === slides.length - 1} onClick={() => goTo(index + 1)} className="cal-pv-arrow is-next gtmtrack" content-content="chatbot-fiche-photo">
              <ChevronRightIcon width={18} height={18} />
            </button>
            <span className="cal-pv-counter">
              {index + 1} / {slides.length}
            </span>
          </>
        )}
      </div>
      {many && (
        <div ref={thumbsRef} className="cal-pv-thumbs">
          {slides.map((s, i) => (
            <button
              key={s.url}
              type="button"
              aria-label={`Photo ${i + 1}`}
              aria-current={i === index}
              onClick={() => goTo(i)}
              className="cal-pv-thumb gtmtrack"
              content-content="chatbot-fiche-photo"
            >
              <img src={sizedImage(s.url, s.formats, 'thumbnail')} alt="" loading="lazy" draggable={false} />
            </button>
          ))}
        </div>
      )}
      {zoomed !== null && (
        <Lightbox
          slides={slides}
          start={zoomed}
          title={product.title}
          onClose={(i) => {
            setZoomed(null);
            goTo(i, false);
          }}
        />
      )}
    </div>
  );
}

/**
 * The footer, like the product page's buy bar on a phone: the format field (which
 * unfolds the formats above it), then the quantity and "Ajouter au panier".
 */
function BuyBar({
  variants,
  variant,
  onChoose,
}: {
  variants: ProductVariant[];
  variant: ProductVariant;
  onChoose: (v: ProductVariant) => void;
}): JSX.Element {
  const { onAddToCart } = useChatContext();
  const [listOpen, setListOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const several = variants.length > 1;
  const available = variant.availability === 'AVAILABLE';
  const price = finalPrice(variant);

  async function handleAddToCart() {
    if (!available || adding) return;
    setAdding(true);
    try {
      await onAddToCart(variant.id, quantity);
    } finally {
      setAdding(false);
    }
  }

  const fieldText = (
    <>
      <span className="cal-pv-field-name">
        <b>{formatName(variant.title)}</b>
        {formatDetail(variant.title) && ` · ${formatDetail(variant.title)}`}
      </span>
      {price != null && <span className="cal-pv-field-price">{formatPrice(price)}</span>}
    </>
  );

  return (
    <footer className="cal-pv-buy">
      {listOpen && (
        <div className="cal-pv-formats">
          <div className="cal-pv-formats-head">
            <b>Sélectionnez un format</b>
            <button type="button" aria-label="Fermer la liste des formats" onClick={() => setListOpen(false)}>
              <XIcon width={14} height={14} />
            </button>
          </div>
          <div role="radiogroup" aria-label="Format" className="cal-pv-formats-list">
            {variants.map((v, i) => {
              const vPrice = finalPrice(v);
              const detail = formatDetail(v.title);
              return (
                <button
                  key={`${v.id}-${i}`}
                  type="button"
                  role="radio"
                  aria-checked={v === variant}
                  disabled={v.availability !== 'AVAILABLE'}
                  onClick={() => {
                    onChoose(v);
                    setListOpen(false);
                  }}
                  className="cal-pv-format gtmtrack"
                  content-content="chatbot-fiche-format"
                >
                  <span className="cal-pv-format-name">
                    <b>{formatName(v.title)}</b>
                    {detail && ` · ${detail}`}
                  </span>
                  <span className="cal-pv-format-price">
                    {v.availability !== 'AVAILABLE' && <span className="cal-pcard-oos">Rupture</span>}
                    {discountTag(v)}
                    {vPrice != null && formatPrice(vPrice)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {several ? (
        <button type="button" aria-expanded={listOpen} onClick={() => setListOpen(!listOpen)} className="cal-pv-field gtmtrack" content-content="chatbot-fiche-formats">
          <ChevronRightIcon width={16} height={16} />
          {fieldText}
        </button>
      ) : (
        <div className="cal-pv-field is-static">{fieldText}</div>
      )}
      <div className="cal-pv-buyrow">
        <div className="cal-pv-qty">
          <button type="button" aria-label="Diminuer la quantité" disabled={quantity <= 1} onClick={() => setQuantity(quantity - 1)}>
            −
          </button>
          <span aria-label="Quantité">{quantity}</span>
          <button
            type="button"
            aria-label="Augmenter la quantité"
            disabled={quantity >= 99}
            onClick={() => setQuantity(quantity + 1)}
            className="gtmtrack"
            content-content="chatbot-fiche-quantite"
          >
            +
          </button>
        </div>
        <button type="button" onClick={handleAddToCart} disabled={!available || adding} className="cal-pv-add gtmtrack" content-content="chatbot-fiche-panier">
          {adding ? '…' : 'Ajouter au panier'}
        </button>
      </div>
    </footer>
  );
}

interface SheetSection {
  title: string;
  body: ReactNode;
  /** its click's name for GTM: chatbot-fiche-<track> */
  track: string;
}

/**
 * Every long section the payload carries, in one reading order, whatever its shape:
 * the description and the catalog's tabs come as markdown; some products instead
 * send the composition, usages and warnings as structured fields.
 */
function sheetSections(p: Product): SheetSection[] {
  // reading order: description, ingredients, usage, anything else (Format Tisane…), precautions last
  const rank = (t: string) => (/ingr|compo/i.test(t) ? 1 : /conseil|utilis/i.test(t) ? 2 : /pr[ée]caution/i.test(t) ? 4 : 3);
  const TRACK = ['description', 'ingredients', 'utilisation', 'autre', 'precautions'];
  const out: Array<Omit<SheetSection, 'track'> & { rank: number }> = [];
  if (p.description) out.push({ title: 'Description', rank: 0, body: <CatalogMarkdown source={p.description} /> });
  for (const t of p.tabs ?? []) {
    out.push({ title: t.title.trim(), rank: rank(t.title), body: <CatalogMarkdown source={t.content} /> });
  }
  if (p.composition_summary || p.ingredients_list?.length) {
    out.push({
      title: 'Composition',
      rank: 1,
      body: (
        <div className="cal-pv-md">
          {p.composition_summary && <p>{p.composition_summary}</p>}
          {p.ingredients_list && p.ingredients_list.length > 0 && (
            <table>
              <tbody>
                {p.ingredients_list.map((ing, i) => (
                  <tr key={i}>
                    <td>
                      {ing.pinyin}
                      <br />
                      <em>{ing.latin}</em>
                    </td>
                    <td>{ing.part}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ),
    });
  }
  if (p.usages?.length) out.push({ title: "Conseils d'utilisation", rank: 2, body: <Paragraphs items={p.usages} /> });
  if (p.warnings?.length) out.push({ title: "Précautions d'emploi", rank: 4, body: <Paragraphs items={p.warnings} /> });
  return out.sort((a, b) => a.rank - b.rank).map((s) => ({ ...s, track: TRACK[s.rank] }));
}

/**
 * Accordions, all closed at first. Opening one closes the others and brings the
 * reader to its start (its header at the top of the sheet).
 */
function SheetSections({ sections }: { sections: SheetSection[] }): JSX.Element | null {
  const [open, setOpen] = useState<number | null>(null);
  // the one folding away: kept until its fold has played, then gone like any closed one
  const [closing, setClosing] = useState<number | null>(null);
  const headers = useRef<Array<HTMLButtonElement | null>>([]);
  const panels = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (closing == null) return;
    const t = setTimeout(() => setClosing(null), FOLD_MS + 40);
    return () => clearTimeout(t);
  }, [closing]);

  function toggle(i: number) {
    const was = open;
    const still = prefersReducedMotion();
    setOpen(was === i ? null : i);
    setClosing(still ? null : was);
    if (was === i) return;
    // Its title glides to the top of the sheet, to where it will be once the one above (if
    // the open one was above) has folded — the glide, the fold and the unfold in step.
    const header = headers.current[i];
    const sheet = header?.closest<HTMLElement>('.cal-pv-body');
    if (!header || !sheet) return;
    const folding = was != null && was < i ? (panels.current[was]?.getBoundingClientRect().height ?? 0) : 0;
    const to = sheet.scrollTop + header.getBoundingClientRect().top - sheet.getBoundingClientRect().top - folding;
    glide(sheet, to, still ? 0 : FOLD_MS);
  }

  if (sections.length === 0) return null;
  return (
    <div className="cal-pv-sections">
      {sections.map((s, i) => (
        <div key={i} className={`cal-pv-acc${open === i ? ' is-open' : ''}`}>
          <button
            ref={(el) => {
              headers.current[i] = el;
            }}
            type="button"
            aria-expanded={open === i}
            className="gtmtrack"
            content-content={`chatbot-fiche-${s.track}`}
            onClick={() => toggle(i)}
          >
            {s.title}
            <ChevronRightIcon width={16} height={16} />
          </button>
          {(open === i || closing === i) && (
            <div
              ref={(el) => {
                panels.current[i] = el;
              }}
              className={`cal-pv-acc-panel${open === i ? '' : ' is-closing'}`}
              aria-hidden={open === i ? undefined : true}
            >
              <div>
                <div className="cal-pv-acc-body">{s.body}</div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** a section's unfold / fold (index.css .cal-pv-acc-panel) */
const FOLD_MS = 300;

/** Scrolls `el` to `to` over `ms`, frame by frame (easing out), so it keeps step with a fold. */
function glide(el: HTMLElement, to: number, ms: number): void {
  const from = el.scrollTop;
  if (!ms || typeof requestAnimationFrame !== 'function') {
    el.scrollTop = to;
    return;
  }
  const t0 = performance.now();
  const step = (now: number) => {
    const k = Math.min(1, (now - t0) / ms);
    el.scrollTop = from + (to - from) * (1 - (1 - k) ** 3);
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function CatalogMarkdown({ source }: { source: string }): JSX.Element {
  return <MarkdownRenderer source={tidyCatalogMarkdown(source)} className="cal-pv-md" />;
}

function Paragraphs({ items }: { items: string[] }): JSX.Element {
  return (
    <div className="cal-pv-md">
      {items.map((text, i) => (
        <p key={i}>{text}</p>
      ))}
    </div>
  );
}

/**
 * The catalog's markdown as the backend relays it: a composition table flattened into
 * "Astragale (Huang Qi)\n1200 mg" paragraphs, and bare file names ("14 Préparation.jpg")
 * where an image was lost. Rebuild the table, drop the file names.
 * ponytail: repairs the payload client-side; drop once the backend sends the table intact.
 */
export function tidyCatalogMarkdown(source: string): string {
  const out: string[] = [];
  let rows: string[] = [];
  const flush = () => {
    if (rows.length) out.push(['| Ingrédient | Quantité |', '| --- | ---: |', ...rows].join('\n'));
    rows = [];
  };
  for (const raw of source.replace(/\r/g, '').split(/\n{2,}/)) {
    const para = raw.trim();
    if (!para || (/^[^\n]*\.(jpe?g|png|webp)$/i.test(para) && !para.includes(']('))) continue;
    const pair = /^([^\n|]+)\n(\d[\d\s.,]*\s?m?g)$/.exec(para);
    if (pair) {
      rows.push(`| ${pair[1].trim()} | ${pair[2].trim()} |`);
      continue;
    }
    flush();
    out.push(para);
  }
  flush();
  return out.join('\n\n');
}

function CheckIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Stars({ rating }: { rating: number }): JSX.Element {
  const full = Math.floor(rating);
  return (
    <span className="tw-inline-flex tw-gap-0.5" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg
          key={i}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill={i < full ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: i < full ? '#7a9858' : '#e4ebd9' }}
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

/**
 * `product.effects` used to be slugs ("detoxifie-lorganisme") and are now prose
 * ("Réduit la fatigue"). Prose only gets its first letter capitalised; a slug gets
 * spaces for hyphens too — lossy on purpose: its accents and apostrophes were lost
 * upstream, and a wrong guess reads worse than plain, unaccented copy.
 */
function humanizeEffect(effect: string): string {
  const text = /\s/.test(effect) ? effect : effect.replace(/-/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}
